import i18n from '@opsbase/lib-i18n';
import useSelf from '@opsbase/react-ctrl-self/useSelf';
import useOpDefinitionFindWithQuestionSetsById from '@opsbase/react-gql-hooks/opdefinition/useOpDefinitionFindWithQuestionSetsById';
import useOpDefinitionUpdate from '@opsbase/react-gql-hooks/opdefinition/useOpDefinitionUpdate';
import useCallforward from '@opsbase/react-hooks/useCallforward/useCallforward';
import Dialog from '@opsbase/web-dialogs/Dialog';
import DialogSize from '@opsbase/web-dialogs/Dialog/DialogSize';
import useParams from '@opsbase/web-nav/useParams';
import OpScheduleCalendar from '@opsbase/web-op-scheduler';
import React, { useCallback, useMemo, useRef } from 'react';
import OpDefinitionEditCallToPublish from './OpDefinitionEditCallToPublish';
import OpDefinitionEditFormTab from './OpDefinitionEditFormTab';
import OpDefinitionEditHistoryTab from './OpDefinitionEditHistoryTab';
import OpDefinitionEditIssuesTab from './OpDefinitionEditIssuesTab';
import OpDefinitionEditOverviewTab from './OpDefinitionEditOverviewTab';
import OpDefinitionEditSettingsTab from './OpDefinitionEditSettingsTab';
import OpDefinitionEditWorkflowsTab from './OpDefinitionEditWorkflowsTab';
export function OpDefinitionEditDialog() {
    let { id, tab = 'overview' } = useParams();
    const changeTabFncRef = useRef();
    const updateOpDefinition = useOpDefinitionUpdate();
    const self = useSelf();
    const { loading, data } = useOpDefinitionFindWithQuestionSetsById({
        variables: { id },
    });
    const { lockEditing, lockEditingToPrimaryAdmins } = data !== null && data !== void 0 ? data : {};
    const editablePermissions = useMemo(() => {
        var _a, _b;
        const isEditor = ((_a = data === null || data === void 0 ? void 0 : data.createdBy) === null || _a === void 0 ? void 0 : _a.id) === self.id || !!((_b = data === null || data === void 0 ? void 0 : data.editorStaff) === null || _b === void 0 ? void 0 : _b.find(z => z.id === self.id));
        const canEdit = self.isPrimary || self.isSuperUser || !lockEditingToPrimaryAdmins || isEditor;
        const canEditWithLock = isEditor || self.isPrimary || self.isSuperUser ? canEdit : !lockEditing && canEdit;
        return {
            canPublish: canEdit,
            canEditForm: canEdit,
            canEditSchedules: canEditWithLock,
            canChangeTitle: canEdit,
            canEditBusinessRules: canEditWithLock,
            canEditHistory: canEdit,
            canEditIssues: canEdit,
            canEditSettings: canEditWithLock,
        };
    }, [data, lockEditing, lockEditingToPrimaryAdmins, self.id, self.isPrimary, self.isSuperUser]);
    const isPublishRequired = !!(data === null || data === void 0 ? void 0 : data.requiresPublishing);
    const handleChange = useCallback((patch) => {
        var _a, _b, _c, _d, _e, _f;
        if ((_a = patch === null || patch === void 0 ? void 0 : patch.places) === null || _a === void 0 ? void 0 : _a.length) {
            patch.places = patch.places.map(p => ({ id: p.id }));
        }
        if ((_b = patch === null || patch === void 0 ? void 0 : patch.teams) === null || _b === void 0 ? void 0 : _b.length) {
            patch.teams = patch.teams.map(p => ({ id: p.id }));
        }
        if ((_c = patch === null || patch === void 0 ? void 0 : patch.positions) === null || _c === void 0 ? void 0 : _c.length) {
            patch.positions = patch.positions.map(p => ({ id: p.id }));
        }
        if ((_d = patch === null || patch === void 0 ? void 0 : patch.approvalStaff) === null || _d === void 0 ? void 0 : _d.length) {
            patch.approvalStaff = patch.approvalStaff.map(p => ({ id: p.id }));
        }
        if ((_e = patch === null || patch === void 0 ? void 0 : patch.approvalTeams) === null || _e === void 0 ? void 0 : _e.length) {
            patch.approvalTeams = patch.approvalTeams.map(p => ({ id: p.id }));
        }
        if ((_f = patch === null || patch === void 0 ? void 0 : patch.approvalPositions) === null || _f === void 0 ? void 0 : _f.length) {
            patch.approvalPositions = patch.approvalPositions.map(p => ({ id: p.id }));
        }
        data &&
            updateOpDefinition({
                id: data.id,
                patch,
            });
    }, [data, updateOpDefinition]);
    const handleChangeField = useCallforward(field => value => handleChange({ [field]: value }), [handleChange]);
    const forceUpdate = React.useReducer(() => ({}), {})[1];
    return useMemo(() => {
        if (loading || !data) {
            return null;
        }
        return (React.createElement(Dialog, { size: DialogSize.XFULL_YFULL, sysLangPicker: true, editableTitle: editablePermissions.canChangeTitle, title: data ? data.title : i18n.t('loading'), titleAlt: data ? data.titleAlt || {} : {}, onChangeTitle: handleChangeField('title'), onChangeTitleAlt: handleChangeField('titleAlt'), refChangeTab: changeTabFncRef, childrenRightOfTitle: React.createElement(OpDefinitionEditCallToPublish, { opDefinition: data, isPublishRequired: isPublishRequired, disabled: !editablePermissions.canPublish }), padded: false, scroll: true, tabs: [
                {
                    default: tab === 'overview',
                    label: i18n.t('overview'),
                    route: 'overview',
                    component: () => React.createElement(OpDefinitionEditOverviewTab, { opDefinition: data }),
                },
                {
                    default: tab === 'form',
                    label: i18n.t('form'),
                    route: 'form',
                    component: () => (React.createElement(OpDefinitionEditFormTab, { opDefinition: data, isEditable: editablePermissions.canEditForm })),
                },
                {
                    default: tab === 'schedule',
                    label: i18n.t('schedule'),
                    route: 'schedule',
                    component: () => (React.createElement(OpScheduleCalendar, { opDefinition: data, onChange: forceUpdate, editable: editablePermissions.canEditSchedules })),
                },
                {
                    default: tab === 'history',
                    label: i18n.t('history'),
                    route: 'history',
                    component: () => (React.createElement(OpDefinitionEditHistoryTab, { opDefinition: data, editable: editablePermissions.canEditHistory })),
                },
                {
                    default: tab === 'issues',
                    label: i18n.t('issues'),
                    route: 'issues',
                    component: () => (React.createElement(OpDefinitionEditIssuesTab, { opDefinition: data, disabled: !editablePermissions.canEditIssues })),
                },
                {
                    default: tab === 'settings',
                    label: i18n.t('settings'),
                    route: 'settings',
                    component: () => (React.createElement(OpDefinitionEditSettingsTab, { opDefinition: data, onChange: handleChange, disabled: !editablePermissions.canEditSettings })),
                },
                ...(editablePermissions.canEditBusinessRules
                    ? [
                        {
                            default: tab === 'business-rules',
                            label: i18n.t('business-rules'),
                            route: 'business-rules',
                            component: () => (React.createElement(OpDefinitionEditWorkflowsTab, { definition: data, type: 'opdefinition', editable: editablePermissions.canEditBusinessRules })),
                        },
                    ]
                    : []),
            ] }));
    }, [
        loading,
        data,
        editablePermissions.canChangeTitle,
        editablePermissions.canPublish,
        editablePermissions.canEditBusinessRules,
        editablePermissions.canEditForm,
        editablePermissions.canEditSchedules,
        editablePermissions.canEditHistory,
        editablePermissions.canEditIssues,
        editablePermissions.canEditSettings,
        handleChangeField,
        isPublishRequired,
        tab,
        forceUpdate,
        handleChange,
    ]);
}
export default OpDefinitionEditDialog;
